import { BADGE_ADMIN_PASSWORD, redactBadgeAdminPassword } from "./badgeAdmin";
import { APP_MODE_LABELS, BadgeAppMode, BadgeIdentity } from "./badgeData";
import { badgeRestorePlan } from "./badgeRestore";

// Ported from badge-firmware/tools/badge-provision/index.html. Keep the serial
// protocol and recovery behavior aligned with that source when firmware
// changes; this module only replaces the standalone page's DOM/form boundary.
const BAUD = 115200;
export class BadgeOwnershipError extends Error {
  constructor() {
    super(
      "This badge is not yours! Check that you connected your own badge. If this is your badge, bring it to the Badge Help Desk so we can fix its registration."
    );
    this.name = "BadgeOwnershipError";
  }
}

const IDENTITY_PATH = "/littlefs/identity.json";
// BLE sync staging file (firmware kFsSnapshotTmpPath, mirrored under a
// root-relative key). Its transport header carries the raw sync credential.
const SYNC_STAGING_KEY = ".sync_snapshot.json";
const CHUNK = 128;
const CHUNK_PAUSE_MS = 20;
const REBOOT_TIMEOUT_MS = 15000;

// Only complete, known diagnostic lines may reach the browser console. Raw
// replies can contain credentials, identity/contact data, or wallet backups.
const SYNC_DIAGNOSTIC_LINES = [
  /^no room for a snapshot: largest block \d+, free \d+, need \d+$/,
  /^# heap tight: cap \d+ not \d+ \(largest \d+, free \d+\)$/,
  /^snapshot failed \(out of memory\)$/,
  /^bytes=\d+ crc32=[0-9a-f]{8} complete=(yes|no) elapsed_ms=\d+$/,
  /^seen=\d+ text=\d+ binary=\d+ too_big=\d+ dropped=\d+ unreadable=\d+ truncated=\d+ path_skipped=\d+$/,
  /^provisioned=[01]$/,
  /^APP MODE (?:OK )?(default|public|all)$/,
  /^BADGE_TOKEN (MISSING|READY|OK|FAIL)$/,
  /^BADGE_PROFILE (READY|OK|FAIL)$/,
  /^Unrecognized command$/,
  /^Command returned non-zero error code: 0x[0-9a-f]+ \([A-Z_]+\)$/,
];
const SYNC_DIAGNOSTIC_COMMAND =
  /^(prov show|appmode|badge_token get|snapshot|badge_profile apply \d+)$/;

const LIMITS: Record<string, number> = {
  badge_id: 63,
  display_name: 39,
  account_email: 63,
  claim_id: 23,
  net_email: 63,
  net_phone: 19,
  net_linkedin: 39,
  net_discord: 39,
  net_instagram: 31,
  net_x: 31,
};

const LATIN1 = /^[\x20-\x7E\xA0-\xFF]*$/;
const pause = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

interface BadgeSerialPort {
  readable: ReadableStream<Uint8Array> | null;
  writable: WritableStream<Uint8Array> | null;
  connected?: boolean;
  open(options: { baudRate: number; bufferSize: number }): Promise<void>;
  close(): Promise<void>;
  addEventListener(
    type: "disconnect",
    listener: EventListener,
    options?: AddEventListenerOptions
  ): void;
  removeEventListener(type: "disconnect", listener: EventListener): void;
}

interface BadgeSerialApi {
  requestPort(): Promise<BadgeSerialPort>;
  getPorts(): Promise<BadgeSerialPort[]>;
}

const serialApi = (): BadgeSerialApi | null =>
  (navigator as unknown as { serial?: BadgeSerialApi }).serial ?? null;

export type BadgeInspection = {
  provisioned: boolean;
  mac: string;
  appMode?: BadgeAppMode;
  identity?: Partial<BadgeIdentity>;
};

export type BadgeProgramResult = {
  mac: string;
  identity: BadgeIdentity;
  appMode: BadgeAppMode;
};

export class BadgeOperationError extends Error {
  connectionRecovered: boolean;

  constructor(message: string, connectionRecovered: boolean) {
    super(message);
    this.name = "BadgeOperationError";
    this.connectionRecovered = connectionRecovered;
  }
}

export class BadgeAlreadyProvisionedError extends Error {
  badgeId: string;

  constructor(badgeId: string) {
    super(
      `This badge is already assigned to ${badgeId}. Confirm the overwrite before programming it for someone else.`
    );
    this.name = "BadgeAlreadyProvisionedError";
    this.badgeId = badgeId;
  }
}

export const parseKeyValueReply = (reply: string): Record<string, string> => {
  const values: Record<string, string> = {};
  reply
    .replace(/\r/g, "")
    .split("\n")
    .forEach((rawLine) => {
      const match = rawLine.trim().match(/^([a-z_]+)=(.*)$/);
      if (match) values[match[1]] = match[2];
    });
  return values;
};

export const validateBadgeIdentity = (identity: BadgeIdentity): void => {
  Object.entries(LIMITS).forEach(([key, max]) => {
    const value = String(
      (identity as unknown as Record<string, string | number | undefined>)[
        key
      ] ?? ""
    );
    if (new TextEncoder().encode(value).length > max) {
      throw new Error(
        `${key} is too long for the badge (maximum ${max} bytes)`
      );
    }
    if (!LATIN1.test(value)) {
      throw new Error(`${key} contains unsupported characters`);
    }
  });

  if (!identity.display_name.trim())
    throw new Error("display_name is required");
  if (!identity.account_email.trim())
    throw new Error("account_email is required");
  if (!identity.badge_id.trim()) throw new Error("badge_id is required");
  if (!Number.isFinite(identity.attendee_id)) {
    throw new Error("attendee_id must be a number");
  }
};

type BadgeProvisionerOptions = {
  onLog?: (text: string) => void;
  onSyncDiagnostic?: (text: string) => void;
  onConnectionLost?: (error?: Error) => void;
};

export class BadgeProvisioner {
  private port: BadgeSerialPort | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private readLoopPromise: Promise<void> | null = null;
  private cleanupPromise: Promise<void> | null = null;
  private rx = "";
  private privateReplies = false;
  private diagnosticLine = "";
  private diagnosticLineTooLong = false;
  private watchedPort: BadgeSerialPort | null = null;
  private portDisconnectHandler: EventListener | null = null;
  private disconnectPoll: number | null = null;
  private readonly onLog: (text: string) => void;
  private readonly onSyncDiagnostic: (text: string) => void;
  private readonly onConnectionLost: (error?: Error) => void;

  constructor(options: BadgeProvisionerOptions = {}) {
    this.onLog = options.onLog ?? (() => undefined);
    this.onSyncDiagnostic = options.onSyncDiagnostic ?? (() => undefined);
    this.onConnectionLost = options.onConnectionLost ?? (() => undefined);
  }

  static isSupported(): boolean {
    return serialApi() != null;
  }

  get connected(): boolean {
    return this.port != null && this.writer != null;
  }

  async connect(): Promise<void> {
    const serial = serialApi();
    if (!serial) throw new Error("Web Serial is not supported in this browser");

    try {
      const ports = (await serial.getPorts()).filter(
        (port) => port.connected !== false
      );
      const port = ports.length === 1 ? ports[0] : await serial.requestPort();
      await port.open({ baudRate: BAUD, bufferSize: 4096 });
      this.port = port;
      if (!port.writable) throw new Error("Badge serial port is not writable");
      this.writer = port.writable.getWriter();
      this.readLoopPromise = this.readLoop(port);
      this.startDisconnectWatch(port);

      // Prove the badge is responding instead of treating an open but silent
      // serial port as a successful connection.
      if (!(await this.sync(3))) {
        throw new Error(
          "The serial port opened, but the badge console did not respond"
        );
      }
    } catch (error) {
      await this.cleanup();
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    await this.cleanup();
  }

  async rebootForSync(): Promise<void> {
    this.privateReplies = true;
    this.rx = "";
    try {
      this.onSyncDiagnostic("Restarting badge before sync.");
      await this.sendLine("reboot");
      await this.waitFor("rebooting...", 3000);
      await this.waitFor("badge> ", REBOOT_TIMEOUT_MS);
      this.onSyncDiagnostic("Badge restarted and is ready to sync.");
    } catch {
      throw new Error(
        "The badge did not come back after restarting. Unplug it, reconnect, and try again."
      );
    } finally {
      this.rx = "";
    }
  }

  async inspect(): Promise<BadgeInspection> {
    try {
      if (!(await this.sync(2))) throw new Error("Badge did not respond");
      const reply = await this.ask("prov show", 8000);
      if (/Unrecognized command/.test(reply)) {
        throw new Error(
          "This badge firmware cannot read provisioning data; flash the current build first"
        );
      }

      const values = parseKeyValueReply(reply);
      if (!["0", "1"].includes(values.provisioned))
        throw new Error("The badge did not report its registration state");
      const modeReply = await this.ask("appmode", 5000);
      const modeMatch = modeReply.match(/\bAPP MODE (default|public|all)\b/);
      const appMode = modeMatch?.[1] as BadgeAppMode | undefined;
      const inspection: BadgeInspection = {
        provisioned: values.provisioned === "1",
        mac: values.mac || "unknown",
        appMode,
      };

      if (inspection.provisioned) {
        inspection.identity = {
          version: 1,
          badge_id: values.badge_id,
          attendee_id: Number(values.attendee_id),
          role: values.role,
          display_name: values.display_name,
          account_email: values.account_email,
          provisioned_unix: Number(values.provisioned_unix || 0),
          claim_id: values.claim_id,
          net_email: values.net_email,
          net_phone: values.net_phone,
          net_linkedin: values.net_linkedin,
          net_discord: values.net_discord,
          net_instagram: values.net_instagram,
          net_x: values.net_x,
        };
      }
      return inspection;
    } catch (error) {
      const recovered = await this.recoverConnection(false);
      throw new BadgeOperationError(
        error instanceof Error ? error.message : String(error),
        recovered
      );
    }
  }

  async program(
    identity: BadgeIdentity,
    appMode: BadgeAppMode,
    options: { allowOverwrite?: boolean; requireClean?: boolean } = {}
  ): Promise<BadgeProgramResult> {
    validateBadgeIdentity(identity);
    if (!(appMode in APP_MODE_LABELS)) {
      throw new Error("Choose a valid badge app mode");
    }

    let uploadStarted = false;
    try {
      // Reading immediately before every write prevents an operator from
      // silently replacing a badge already allocated to someone else.
      const existing: BadgeInspection & { factoryClean?: boolean } =
        options.requireClean
          ? await this.inspectForSetup()
          : await this.inspect();
      if (options.requireClean && !existing.factoryClean)
        throw new Error(
          "Factory reset this badge before setting it up for a hacker."
        );
      const existingId = existing.identity?.badge_id;
      if (
        existing.provisioned &&
        existingId !== identity.badge_id &&
        !options.allowOverwrite
      ) {
        throw new BadgeAlreadyProvisionedError(
          existingId || "an unknown badge ID"
        );
      }

      const jsonText = JSON.stringify(identity);
      const bytes = new TextEncoder().encode(jsonText);

      // A badge unplugged mid-upload may still be consuming bytes inside
      // `put`. Re-sync and, if needed, finish that stale read before retrying.
      if (!(await this.sync(2))) {
        await this.unwedge();
        if (!(await this.sync(2))) {
          throw new Error("Badge stopped responding; reconnect it");
        }
      }

      // Fail before writing identity on old firmware or a password mismatch.
      await this.requireAdminAccess();
      uploadStarted = true;
      await this.sendLine(`put ${IDENTITY_PATH} ${bytes.length}`);
      try {
        await this.waitFor("READY", 5000);
      } catch {
        throw new Error(`Badge did not accept the upload; ${this.tail(3)}`);
      }
      await this.writeBytes(bytes);
      try {
        await this.waitFor(`OK ${bytes.length}`, 8000);
      } catch {
        throw new Error(`Badge upload did not complete; ${this.tail(3)}`);
      }

      await this.waitFor("badge> ", 5000).catch(() => undefined);

      let readBack: string;
      try {
        readBack = await this.ask(`cat ${IDENTITY_PATH}`, 8000);
      } catch {
        throw new Error(`Could not read identity.json back; ${this.tail(3)}`);
      }
      const start = readBack.indexOf("{");
      const end = readBack.lastIndexOf("}");
      const onBadge =
        start >= 0 && end > start ? readBack.slice(start, end + 1) : "";
      if (onBadge !== jsonText) {
        throw new Error(
          onBadge
            ? `identity.json on the badge does not match what was sent (${onBadge.length} of ${jsonText.length} bytes)`
            : `Could not read identity.json back; ${this.summarize(readBack)}`
        );
      }

      let applyReply: string;
      try {
        applyReply = await this.ask("prov apply", 8000);
      } catch {
        throw new Error(`No reply to 'prov apply'; ${this.tail(3)}`);
      }
      if (/PROV FAIL/.test(applyReply)) {
        const reason =
          applyReply.match(/PROV FAIL ([^\r\n]*)/)?.[1] ?? "unknown";
        throw new Error(`Badge rejected the identity: ${reason}`);
      }
      if (/Unrecognized command/.test(applyReply)) {
        throw new Error(
          "This badge firmware has no 'prov' command; flash the current build first"
        );
      }
      if (!/PROV OK/.test(applyReply)) {
        throw new Error(
          `Unexpected reply to 'prov apply'; ${this.summarize(applyReply)}`
        );
      }
      const mac = applyReply.match(/mac=([0-9a-f:]{17})/i)?.[1] ?? "unknown";

      let modeReply: string;
      try {
        modeReply = await this.askAdmin(`appmode ${appMode}`, 5000);
      } catch (error) {
        throw new Error(
          `Identity was programmed, but app mode was not confirmed; ${redactBadgeAdminPassword(
            error instanceof Error ? error.message : String(error)
          )}`
        );
      }
      if (/Unrecognized command/.test(modeReply)) {
        throw new Error(
          "Identity was programmed, but this firmware has no 'appmode' command"
        );
      }
      if (!new RegExp(`\\bAPP MODE OK ${appMode}\\b`).test(modeReply)) {
        throw new Error(
          `Identity was programmed, but app mode was not confirmed; ${this.summarize(
            modeReply
          )}`
        );
      }

      return { mac, identity, appMode };
    } catch (error) {
      if (error instanceof BadgeAlreadyProvisionedError) throw error;
      // inspect() has already attempted a non-destructive recovery. The extra
      // unwedge here is specifically for failed uploads that may have left the
      // firmware inside its fixed-size `put` read.
      const recovered = await this.recoverConnection(uploadStarted);
      throw new BadgeOperationError(
        redactBadgeAdminPassword(
          error instanceof Error ? error.message : String(error)
        ),
        recovered
      );
    }
  }

  async writeUploadToken(rawToken: string): Promise<void> {
    if (!/^[0-9a-f]{64}$/.test(rawToken))
      throw new Error("Invalid badge token format");
    // Never forward credential replies (including late replies) to console logs
    // or the registration diagnostics panel for this connection.
    this.privateReplies = true;
    this.rx = "";
    try {
      await this.sendLine("badge_token set");
      await this.waitFor("BADGE_TOKEN READY", 5000);
      await this.writeBytes(new TextEncoder().encode(rawToken));
      await this.waitFor("badge> ", 12000);
      const reply = await this.ask("badge_token get", 5000);
      if (
        reply.match(/(?:^|\n)BADGE_TOKEN ([0-9a-f]{64})(?:\r?\n)/)?.[1] !==
        rawToken
      ) {
        throw new Error("Token verification failed");
      }
    } catch {
      throw new Error(
        "Badge sync setup could not be verified. Reconnect and program the badge again using current firmware."
      );
    } finally {
      this.rx = "";
    }
  }

  async readSyncCredentials(expectedClaimId?: number): Promise<{
    claimId: number;
    badgeId: string;
    displayName: string;
    rawToken: string;
  }> {
    this.privateReplies = true;
    this.rx = "";
    try {
      const inspection = await this.inspect();
      const claimId = Number(inspection.identity?.claim_id);
      if (
        !inspection.provisioned ||
        !Number.isSafeInteger(claimId) ||
        claimId <= 0
      )
        throw new Error("Missing badge identity");
      if (expectedClaimId !== undefined && claimId !== expectedClaimId)
        throw new BadgeOwnershipError();
      const reply = await this.ask("badge_token get", 5000);
      const rawToken = reply.match(
        /(?:^|\n)BADGE_TOKEN ([0-9a-f]{64})(?:\r?\n)/
      )?.[1];
      if (!rawToken) throw new Error("Missing badge token");
      return {
        claimId,
        badgeId: inspection.identity?.badge_id || "",
        displayName: inspection.identity?.display_name || "Unknown attendee",
        rawToken,
      };
    } catch (error) {
      if (error instanceof BadgeOwnershipError) throw error;
      throw new Error(
        "Could not read badge credentials. Reconnect and check that registration and firmware are up to date."
      );
    } finally {
      this.rx = "";
    }
  }

  async applyProfile(profile: Record<string, string>): Promise<void> {
    const before = await this.readSyncCredentials();
    if (
      profile.badge_id !== before.badgeId ||
      profile.claim_id !== String(before.claimId)
    )
      throw new Error(
        "This badge does not match the profile's assigned badge. Visit registration before syncing."
      );
    const bytes = new TextEncoder().encode(JSON.stringify(profile));
    if (bytes.length > 2048) throw new Error("Badge profile is too large.");
    try {
      await this.sendLine(`badge_profile apply ${bytes.length}`);
      await this.waitFor("BADGE_PROFILE READY", 5000);
      await this.writeBytes(bytes);
      if (!(await this.waitFor("badge> ", 12000)).includes("BADGE_PROFILE OK"))
        throw new Error("Profile was not applied");
      const inspection = await this.inspect();
      for (const [key, value] of Object.entries(profile)) {
        if ((inspection.identity as Record<string, unknown>)?.[key] !== value)
          throw new Error("Profile verification failed");
      }
      const after = await this.readSyncCredentials();
      if (after.rawToken !== before.rawToken)
        throw new Error("Credential changed");
    } catch {
      throw new Error(
        "The badge profile update could not be verified. Reconnect and use the latest firmware before retrying."
      );
    } finally {
      this.rx = "";
    }
  }

  async readUpload(expectedClaimId?: number) {
    let stage = "reading sync credentials";
    try {
      const credentials = await this.readSyncCredentials(expectedClaimId);
      stage = "reading the snapshot";
      const reply = await this.ask("snapshot", 30000);
      const line = reply
        .replace(/\r/g, "")
        .split("\n")
        .find((value) => value.trim().startsWith("{"));
      stage = "parsing the snapshot JSON";
      const snapshot = JSON.parse(line || "") as Record<string, unknown>;
      if (!snapshot || Array.isArray(snapshot) || typeof snapshot !== "object")
        throw new Error("Invalid snapshot");
      // A crash mid-BLE-transfer leaves the staging file behind and the
      // serial snapshot mirrors it (the RAM builder sets no skip_path), so a
      // stale staging file trips the credential check below on every sync.
      // It is regenerable transfer staging and never restore input
      // (badgeRestorePlan ignores unknown paths), so exclude it before the
      // safety check and upload rather than failing the sync.
      const stagedFiles = snapshot.files as
        | Record<string, unknown>
        | null
        | undefined;
      if (
        stagedFiles &&
        typeof stagedFiles === "object" &&
        !Array.isArray(stagedFiles)
      ) {
        // Snapshot keys are root-relative (badgeRestorePlan/summary strip an
        // optional /littlefs/ prefix); match either form.
        const stagingKey = Object.keys(stagedFiles).find(
          (path) => path.replace(/^\/littlefs\//, "") === SYNC_STAGING_KEY
        );
        if (stagingKey !== undefined) {
          delete stagedFiles[stagingKey];
          this.onSyncDiagnostic(
            "Excluded stale sync staging file from the upload."
          );
        }
      }
      const json = JSON.stringify(snapshot);
      const bytes = new TextEncoder().encode(json).length;
      stage = "checking snapshot safety and size";
      if (json.includes(credentials.rawToken) || bytes > 65536)
        throw new Error("Unsafe or oversized snapshot");
      this.onSyncDiagnostic(`Snapshot validated (${bytes} bytes).`);
      return { ...credentials, snapshot };
    } catch (error) {
      this.onSyncDiagnostic(`Sync failed while ${stage}.`);
      if (error instanceof BadgeOwnershipError) throw error;
      throw new Error(
        "Could not read a safe snapshot. Reconnect the badge and check that registration and firmware are up to date."
      );
    } finally {
      this.rx = "";
    }
  }

  async restoreBackup(raw: string, expectedClaimId: number): Promise<number> {
    const plan = badgeRestorePlan(raw);
    this.privateReplies = true;
    try {
      await this.readSyncCredentials(expectedClaimId);
      await this.requireAdminAccess();
      const files = [...plan.files];
      if (plan.launcherOrder.length) {
        const current = await this.ask("cat /littlefs/config/system.cfg", 8000);
        const order =
          current
            .split(/\r?\n/)
            .find((line) => line.startsWith("launcher_order="))
            ?.slice(15)
            .split(",") || [];
        // Only merge the order; never import old diagnostics, onboarding or roles.
        const merged = Array.from(new Set([...order, ...plan.launcherOrder]));
        const system = current
          .split(/\r?\n/)
          .filter(
            (line) =>
              /^[a-z0-9_]+=/.test(line) && !line.startsWith("launcher_order=")
          );
        system.push(`launcher_order=${merged.join(",")}`);
        files.push({
          path: "config/system.cfg",
          text: system.join("\n") + "\n",
        });
      }
      if (plan.pals) {
        const reply = await this.ask("cat /littlefs/solana.json", 8000);
        const wallet = JSON.parse(
          reply.slice(reply.indexOf("{"), reply.lastIndexOf("}") + 1)
        );
        if (!wallet || Array.isArray(wallet) || typeof wallet !== "object")
          throw new Error();
        // Keep this physical badge's keys. Import earned pal IDs only.
        wallet.all_items = plan.pals;
        wallet.equipped_pal = 0;
        files.push({ path: "solana.json", text: JSON.stringify(wallet) });
      }
      for (const file of files) {
        const bytes = new TextEncoder().encode(file.text);
        if (bytes.length > 4096) throw new Error();
        await this.sendLine(`put /littlefs/${file.path} ${bytes.length}`);
        await this.waitFor("READY", 5000);
        await this.writeBytes(bytes);
        await this.waitFor(`OK ${bytes.length}`, 8000);
        await this.waitFor("badge> ", 5000);
        const reply = await this.ask(`cat /littlefs/${file.path}`, 8000);
        if (
          !reply
            .replace(/\r/g, "")
            .includes("\n" + file.text.replace(/\r/g, "").trimEnd() + "\n")
        )
          throw new Error();
      }
      // Restart to load the restored launcher and wallet into memory.
      await this.sendLine("reboot");
      await this.cleanup();
      return files.length;
    } catch {
      throw new Error(
        "Registration is saved, but restoring progress did not finish. Keep the badge at the help desk and retry setup from the saved backup."
      );
    } finally {
      this.rx = "";
    }
  }

  async factoryReset(): Promise<void> {
    let identityErased = false;
    try {
      if (!(await this.sync(2))) throw new Error("Badge did not respond");

      const eraseReply = await this.ask("prov erase confirm", 8000);
      if (/Unrecognized command/.test(eraseReply)) {
        throw new Error(
          "This badge firmware has no provisioning erase command"
        );
      }
      if (!/PROV ERASED/.test(eraseReply)) {
        throw new Error(
          `Identity erase was not confirmed; ${this.summarize(eraseReply)}`
        );
      }
      identityErased = true;

      this.rx = "";
      await this.sendLine("factory_reset confirm");
      let resetReply: string;
      try {
        resetReply = await this.waitFor("rebooting...", 8000);
      } catch (error) {
        if (/factory_reset: [^\r\n]*rebooting\.\.\./.test(this.rx)) {
          resetReply = this.rx;
        } else {
          throw error;
        }
      }
      if (/partial/.test(resetReply)) {
        throw new Error(
          "The firmware reported only part of the reset completed"
        );
      }
      if (!/factory_reset: cleared/.test(resetReply)) {
        throw new Error(
          `Factory reset was not confirmed; ${this.summarize(resetReply)}`
        );
      }
      await this.cleanup();
    } catch (error) {
      const recovered = await this.recoverConnection(false);
      const partial = identityErased
        ? " Identity was erased, but the rest of the reset may be incomplete."
        : "";
      throw new BadgeOperationError(
        `${error instanceof Error ? error.message : String(error)}.${partial}`,
        recovered
      );
    }
  }

  async inspectForSetup() {
    this.privateReplies = true;
    try {
      const inspection = await this.inspect();
      if (inspection.provisioned) return { ...inspection, factoryClean: false };
      const reply = await this.ask("snapshot", 30000);
      const snapshot = JSON.parse(
        reply
          .replace(/\r/g, "")
          .split("\n")
          .find((line) => line.trim().startsWith("{")) || ""
      );
      if (
        snapshot?.v !== 1 ||
        snapshot.kind !== "fs_mirror" ||
        snapshot.provisioned !== false ||
        snapshot.complete !== true ||
        !snapshot.files ||
        typeof snapshot.files !== "object" ||
        Array.isArray(snapshot.files)
      )
        throw new Error();
      // Fresh boots write system settings. The device wallet is deliberately
      // preserved by factoryReset; every other file requires an explicit reset.
      return {
        ...inspection,
        factoryClean: Object.keys(snapshot.files).every((path) =>
          ["config/system.cfg", "solana.json"].includes(path)
        ),
      };
    } catch {
      throw new Error(
        "Could not verify that this badge is factory clean. Reconnect with the latest firmware, or factory reset it and try again."
      );
    } finally {
      this.rx = "";
    }
  }

  async unlockPublicApps(): Promise<number> {
    try {
      if (!(await this.sync(2))) throw new Error("Badge did not respond");
      await this.requireAdminAccess();
      const reply = await this.askAdmin("seedall", 8000);
      const count = reply.match(/(?:^|\n)public apps: (\d+) added\r?\n/)?.[1];
      if (count === undefined)
        throw new Error("Badge did not confirm the unlock");
      return Number(count);
    } catch (error) {
      throw new Error(
        `Public app unlock was not confirmed. ${redactBadgeAdminPassword(
          error instanceof Error ? error.message : String(error)
        )}. Reconnect with the latest firmware and try again.`
      );
    }
  }

  async seedVirology(): Promise<void> {
    this.privateReplies = true;
    try {
      if (!(await this.sync(2))) throw new Error();
      await this.requireAdminAccess();
      const seeded = await this.ask("ripple seed", 8000);
      const verified = await this.ask("ripple", 5000);
      if (
        ![seeded, verified].every((reply) =>
          /(?:^|\n)ripple set 0\r?\n/.test(reply)
        )
      )
        throw new Error();
    } catch {
      throw new Error(
        "Virology seeding was not confirmed. Reconnect with the current firmware and try again."
      );
    } finally {
      this.rx = "";
    }
  }

  private async requireAdminAccess(): Promise<void> {
    const reply = await this.askAdmin("appmode check", 5000);
    if (!/(?:^|\n)ADMIN AUTH OK\r?\n/.test(reply)) {
      throw new Error(
        "This badge firmware does not support authenticated admin commands; flash the current engineer firmware first"
      );
    }
  }

  private async askAdmin(command: string, timeoutMs: number): Promise<string> {
    // As with sync tokens, suppress raw replies for the rest of this connection
    // so fragmented or late command echoes cannot leak into diagnostics.
    this.privateReplies = true;
    try {
      const reply = redactBadgeAdminPassword(
        await this.ask(`${command} ${BADGE_ADMIN_PASSWORD}`, timeoutMs)
      );
      if (/(?:^|\n)ADMIN AUTH FAIL\r?\n/.test(reply)) {
        throw new Error("Badge rejected the Admin password");
      }
      if (/Unrecognized command/.test(reply)) {
        throw new Error(
          "This badge firmware does not support authenticated admin commands; flash the current engineer firmware first"
        );
      }
      return reply;
    } catch (error) {
      throw new Error(
        redactBadgeAdminPassword(
          error instanceof Error ? error.message : String(error)
        )
      );
    } finally {
      this.rx = "";
    }
  }

  private log(text: string): void {
    this.onLog(redactBadgeAdminPassword(text));
  }

  private logSyncDiagnostics(text: string): void {
    // USB reads split lines (and tokens) arbitrarily. Never log fragments or
    // retain a large snapshot line; discard its remainder through the next LF.
    for (const part of text.split(/(\n)/)) {
      if (part !== "\n") {
        if (!this.diagnosticLineTooLong) {
          this.diagnosticLine += part;
          if (this.diagnosticLine.length > 1024) {
            this.diagnosticLine = "";
            this.diagnosticLineTooLong = true;
          }
        }
        continue;
      }
      const line = this.diagnosticLine.replace(/\r/g, "").trim();
      if (!this.diagnosticLineTooLong) {
        if (/^BADGE_TOKEN [0-9a-f]{64}$/.test(line)) {
          this.onSyncDiagnostic("BADGE_TOKEN [present; hidden]");
        } else if (
          SYNC_DIAGNOSTIC_LINES.some((pattern) => pattern.test(line))
        ) {
          this.onSyncDiagnostic(redactBadgeAdminPassword(line));
        }
      }
      this.diagnosticLine = "";
      this.diagnosticLineTooLong = false;
    }
  }

  private stopDisconnectWatch(): void {
    if (this.disconnectPoll != null) window.clearInterval(this.disconnectPoll);
    this.disconnectPoll = null;
    if (this.watchedPort && this.portDisconnectHandler) {
      try {
        this.watchedPort.removeEventListener(
          "disconnect",
          this.portDisconnectHandler
        );
      } catch {
        // The read loop remains the final source of disconnect detection.
      }
    }
    this.watchedPort = null;
    this.portDisconnectHandler = null;
  }

  private startDisconnectWatch(activePort: BadgeSerialPort): void {
    this.stopDisconnectWatch();
    this.watchedPort = activePort;
    this.portDisconnectHandler = () => {
      if (this.port === activePort) {
        void this.connectionLost(new Error("USB connection closed"));
      }
    };
    activePort.addEventListener("disconnect", this.portDisconnectHandler, {
      once: true,
    });

    // Browser/OS combinations sometimes deliver the stream error late. Poll
    // the browser's connected port set so the next badge can reconnect quickly.
    this.disconnectPoll = window.setInterval(async () => {
      if (this.port !== activePort) return this.stopDisconnectWatch();
      try {
        const explicitlyDisconnected = activePort.connected === false;
        const available = explicitlyDisconnected
          ? []
          : await serialApi()?.getPorts();
        if (
          this.port === activePort &&
          (explicitlyDisconnected || !available?.includes(activePort))
        ) {
          await this.connectionLost(new Error("USB connection closed"));
        }
      } catch {
        // Direct disconnect and read-loop detection remain active.
      }
    }, 750);
  }

  private async readLoop(activePort: BadgeSerialPort): Promise<void> {
    const decoder = new TextDecoder();
    let failure: Error | undefined;
    try {
      while (this.port === activePort && activePort.readable) {
        const streamReader = activePort.readable.getReader();
        this.reader = streamReader;
        let streamDone = false;
        try {
          for (;;) {
            const { value, done } = await streamReader.read();
            if (done) {
              streamDone = true;
              break;
            }
            const text = decoder.decode(value, { stream: true });
            this.rx += text;
            this.logSyncDiagnostics(text);
            if (this.rx.length > 96 * 1024)
              throw new Error("Badge reply exceeded its limit");
            if (!this.privateReplies) this.log(text);
          }
        } finally {
          streamReader.releaseLock();
          if (this.reader === streamReader) this.reader = null;
        }
        if (streamDone) break;
      }
    } catch (error) {
      failure = error instanceof Error ? error : new Error(String(error));
    } finally {
      if (this.port === activePort) void this.connectionLost(failure);
    }
  }

  // The firmware USB-Serial-JTAG driver has a 256-byte RX ring buffer. Stay
  // under it and pause between chunks so the console does not silently drop
  // the tail of identity.json.
  private async writeBytes(bytes: Uint8Array): Promise<void> {
    const writer = this.writer;
    if (!writer) throw new Error("Badge is not connected");
    for (let index = 0; index < bytes.length; index += CHUNK) {
      await writer.write(bytes.slice(index, index + CHUNK));
      if (index + CHUNK < bytes.length) await pause(CHUNK_PAUSE_MS);
    }
  }

  // ESP line-ending translation turns CRLF into two terminators. A bare CR is
  // required or `put` consumes the extra newline as the first file byte.
  private async sendLine(command: string): Promise<void> {
    const writer = this.writer;
    if (!writer) throw new Error("Badge is not connected");
    if (SYNC_DIAGNOSTIC_COMMAND.test(command)) {
      this.onSyncDiagnostic(`> ${command}`);
    }
    this.log(`> ${command}\n`);
    await writer.write(new TextEncoder().encode(`${command}\r`));
  }

  private waitFor(token: string, timeoutMs: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const poll = () => {
        const index = this.rx.indexOf(token);
        if (index >= 0) {
          const received = this.rx.slice(0, index + token.length);
          this.rx = this.rx.slice(index + token.length);
          resolve(received);
          return;
        }
        if (!this.port) {
          this.onSyncDiagnostic(
            "USB connection closed while waiting for a reply."
          );
          reject(new Error("Badge disconnected"));
          return;
        }
        if (Date.now() - started > timeoutMs) {
          this.onSyncDiagnostic("Timed out waiting for a complete USB reply.");
          reject(new Error(`Timed out waiting for ${JSON.stringify(token)}`));
          return;
        }
        window.setTimeout(poll, 20);
      };
      poll();
    });
  }

  private async ask(command: string, timeoutMs: number): Promise<string> {
    this.rx = "";
    await this.sendLine(command);
    return this.waitFor("badge> ", timeoutMs);
  }

  private async unwedge(): Promise<void> {
    await this.writeBytes(new Uint8Array(1024).fill(0x20));
    const writer = this.writer;
    if (!writer) throw new Error("Badge is not connected");
    await writer.write(new Uint8Array([0x03]));
    await this.sendLine("");
    await pause(150);
  }

  private async sync(attempts: number): Promise<boolean> {
    for (let attempt = 0; attempt < attempts; attempt++) {
      this.rx = "";
      try {
        await this.writeBytes(new Uint8Array([0x03]));
        await this.sendLine("");
        await this.waitFor("badge> ", 1500);
        return true;
      } catch {
        if (!this.port) break;
      }
    }
    return false;
  }

  private tail(lines: number): string {
    const text = redactBadgeAdminPassword(this.rx)
      .replace(/\r/g, "")
      .trim()
      .split("\n")
      .slice(-lines)
      .join(" | ");
    return text ? `badge said: ${text}` : "badge sent nothing";
  }

  private summarize(reply: string): string {
    const text = redactBadgeAdminPassword(reply)
      .replace(/\r/g, "")
      .split("\n")
      .map((line) => line.trim())
      .filter(
        (line) =>
          line && line !== "badge>" && !/^(put|prov|cat|appmode)\b/.test(line)
      )
      .join(" | ");
    return text ? `badge said: ${text}` : "badge said nothing";
  }

  private async recoverConnection(tryUnwedge: boolean): Promise<boolean> {
    if (!this.port) return false;
    if (await this.sync(1)) return true;
    if (tryUnwedge) {
      try {
        await this.unwedge();
      } catch {
        // Continue to cleanup when the badge cannot be recovered.
      }
      if (this.port && (await this.sync(2))) return true;
    }
    await this.cleanup();
    return false;
  }

  private async cleanup(): Promise<void> {
    if (this.cleanupPromise) return this.cleanupPromise;

    const activePort = this.port;
    const activeReader = this.reader;
    const activeWriter = this.writer;
    const activeReadLoop = this.readLoopPromise;
    this.stopDisconnectWatch();
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.readLoopPromise = null;

    this.cleanupPromise = (async () => {
      try {
        await activeReader?.cancel();
      } catch {
        // Best-effort teardown so the port can be reselected.
      }
      try {
        await activeReadLoop;
      } catch {
        // readLoop reports physical disconnects through the callback.
      }
      this.diagnosticLine = "";
      this.diagnosticLineTooLong = false;
      try {
        activeWriter?.releaseLock();
      } catch {
        // The stream may already have released its lock.
      }
      try {
        await activePort?.close();
      } catch {
        // A physically removed port is already closed.
      }
    })();

    try {
      await this.cleanupPromise;
    } finally {
      this.cleanupPromise = null;
    }
  }

  private async connectionLost(error?: Error): Promise<void> {
    if (!this.port) return;
    await this.cleanup();
    this.onConnectionLost(error);
  }
}
